import { Unsubscribe } from 'firebase/database';
import { FirebaseDatabaseClient } from '../clients/firebase-database';
import type { QueryDocumentReturn } from '../clients/firebase-firestore/types';
import { WriteApiService } from '../services/write-api.service';
import { IdHelper } from '../helpers/id.helper';
import { CalendarModel, GratefulStatus, StatusCalendar } from '../models';

const DOCUMENT_NAME = 'calendar';

type GetDocumentPathOptions = {
  id?: string;
  month?: string; // DEPRECATED
};

type WatchEventsFilterBy = {
  key: string;
  value: string;
};

class CalendarRepository {
  static getDocumentPath(collectionPathBase: string[], options?: GetDocumentPathOptions): string[] {
    const path = [...collectionPathBase, DOCUMENT_NAME];

    const { month, id } = options ?? {};

    if (id !== undefined) {
      path.push(id);
    }

    if (month !== undefined) {
      path.push(month);
    }

    return path;
  }

  static watchEvents(
    setData: (data: CalendarModel[]) => void,
    collectionPathBase: string[],
    filterBy: WatchEventsFilterBy
  ): Unsubscribe {
    return FirebaseDatabaseClient.watchDocuments(setData, [...CalendarRepository.getDocumentPath(collectionPathBase)], {
      filterBy: `filters/${filterBy.key}`,
      filterByValue: filterBy.value,
    });
  }

  static async updateStatus(
    collectId: string,
    contactId: string,
    newStatus: StatusCalendar,
    collectionPathBase: string[]
  ): Promise<void> {
    if (WriteApiService.isEnabled()) {
      const [projectId] = collectionPathBase;
      await WriteApiService.updateCalendarStatus(projectId, `${collectId}-${contactId}`, { status: newStatus });
      return;
    }

    await FirebaseDatabaseClient.updatedDocument<CalendarModel>(
      { status: newStatus },
      ...CalendarRepository.getDocumentPath(collectionPathBase),
      `${collectId}-${contactId}`
    );
  }

  static async updateGratefulStatus(
    collectId: string,
    contactId: string,
    newStatus: GratefulStatus,
    collectionPathBase: string[]
  ): Promise<void> {
    if (WriteApiService.isEnabled()) {
      const [projectId] = collectionPathBase;
      await WriteApiService.updateCalendarStatus(projectId, `${collectId}-${contactId}`, {
        gratefulStatus: newStatus,
      });
      return;
    }

    await FirebaseDatabaseClient.updatedDocument<CalendarModel>(
      { gratefulStatus: newStatus },
      ...CalendarRepository.getDocumentPath(collectionPathBase),
      `${collectId}-${contactId}`
    );
  }

  static async changeToAnswered(
    collectId: number,
    contactId: number,
    answeredAt: Date,
    collectionPathBase: string[],
    gratefulEnabled: boolean = true
  ): Promise<void> {
    await FirebaseDatabaseClient.updatedDocument<CalendarModel>(
      {
        status: StatusCalendar.answered,
        answeredAt: answeredAt.toISOString(),
        ...(gratefulEnabled ? { gratefulStatus: GratefulStatus.pending } : {}),
      },
      ...CalendarRepository.getDocumentPath(collectionPathBase),
      `${IdHelper.formatCollectId(collectId)}-${IdHelper.formatContactId(contactId)}`
    );
  }

  static async listByPath(collectionPathBase: string[], options?: GetDocumentPathOptions): Promise<any> {
    return await FirebaseDatabaseClient.getDocumentsByPath<CalendarModel>(
      ...CalendarRepository.getDocumentPath(collectionPathBase, options)
    );
  }

  static async save(id: string, data: CalendarModel, collectionPathBase: string[]): Promise<void> {
    await FirebaseDatabaseClient.updatedDocument<CalendarModel>(
      data,
      ...CalendarRepository.getDocumentPath(collectionPathBase, { id })
    );
  }

  static async remove(id: string, collectionPathBase: string[]): Promise<void> {
    await FirebaseDatabaseClient.removeDocument(...CalendarRepository.getDocumentPath(collectionPathBase, { id }));
  }

  static async removeDocument(collectionPathBase: string[]): Promise<void> {
    await FirebaseDatabaseClient.removeDocument(...collectionPathBase);
  }

  static async getByContactId(
    contactId: number,
    collectionPathBase: [string]
  ): Promise<QueryDocumentReturn<CalendarModel>> {
    const _results = await FirebaseDatabaseClient.filterDocumentsByField<CalendarModel>(
      CalendarRepository.getDocumentPath(collectionPathBase),
      {
        filterBy: `filters/byContact`,
        filterByValue: IdHelper.formatContactId(contactId),
      }
    );

    const results = Object.values(_results ?? {});

    return { results, count: results.length };
  }

  static async getByMonth(month: string, collectionPathBase: [string]): Promise<QueryDocumentReturn<CalendarModel>> {
    const _results = await FirebaseDatabaseClient.filterDocumentsByField<CalendarModel>(
      CalendarRepository.getDocumentPath(collectionPathBase),
      {
        filterBy: `filters/byMonth`,
        filterByValue: month,
      }
    );

    const results = Object.values(_results ?? {});

    return { results, count: results.length };
  }

  static async getByDateStart(date: string, collectionPathBase: [string]): Promise<QueryDocumentReturn<CalendarModel>> {
    const _results = await FirebaseDatabaseClient.filterDocumentsByField<CalendarModel>(
      CalendarRepository.getDocumentPath(collectionPathBase),
      {
        filterBy: `filters/byDateStart`,
        filterByValue: date,
      }
    );

    const results = Object.values(_results ?? {});

    return { results, count: results.length };
  }
}

export { CalendarRepository };
